import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";

import { useThirdPartyOrders } from "react-migration/domains/sites/components/SiteCard/ReportSidebar/AiDash/hooks/useThirdPartyOrder";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Notification } from "react-migration/components/Notification";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { UpgradeCTA } from "react-migration/components/UpgradeCTA";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import {
  AIDASH_CARD_DISPLAY_NAME,
  AIDASH_PROVIDER_NAME,
} from "react-migration/domains/sites/components/SiteCard/ReportSidebar/AiDash/hooks/constants";

import AiDashUpgradeLogo from "public/addons/aidash/aidash-promo-card-hero.png";
import AiDashLogo from "public/addons/aidash/aidash-logo.svg";

import { LayerTypeSelectionSidebarProps } from "../../../types";
import { CollapsibleConsiderationsCard } from "../../../Bundle/Workbench/AssessmentTab/CollapsibleConsiderationsCard";
import { WorkbenchSaveSiteButton } from "../../../Bundle/Workbench/WorkbenchSaveSiteButton";
import { useWorkbench } from "../../../Bundle/Workbench/WorkbenchContext";
import { ThirdPartyOnDemandLayerConfig } from "../ThirdPartyOnDemandLayerType";
import { ThirdPartyOnDemandOrder } from "./ThirdPartyOnDemandOrder";
import { TagColor } from "react-migration/components/Tag";
import clsx from "clsx";

interface UpgradeSectionProps {
  selection: SelectionFeature;
  siteId: string;
  onSelection: (selection: SelectionFeature) => void;
  onSiteSaved?: () => void;
  smallCardView?: boolean;
  labels?: {
    tag: string;
    color: TagColor;
  }[];
}

type ThirdPartyOnDemandReportSelectionSidebarContentProps = Omit<
  LayerTypeSelectionSidebarProps,
  "setDetailSelection" | "onLoadingChange" | "cardExpanded" | "onSelection"
> & {
  smallCardView?: boolean;
  onSelection: (selection: SelectionFeature) => void;
  labels?: {
    tag: string;
    color: TagColor;
  }[];
};

export const ThirdPartyOnDemandReportSelectionSidebar = ({
  cardExpanded,
  ...props
}: LayerTypeSelectionSidebarProps) => {
  const { t } = useTranslation();
  const providerName = (props.layer.layerConfig as ThirdPartyOnDemandLayerConfig).providerName;

  if (!providerName) {
    console.error("`layerConfig.providerName` is required for `ThirdPartyOnDemandReportSelectionSidebar`");
    return null;
  }

  return (
    <CollapsibleConsiderationsCard
      name={`third-party-on-demand-report-layer-card-${providerName}`}
      heading={t(`third_party_on_demand_report_layer.${providerName}.selection_sidebar.card_subtitle`)}
      defaultExpanded={cardExpanded}
      label="New"
    >
      <ThirdPartyOnDemandReportSelectionSidebarContent {...props} />
    </CollapsibleConsiderationsCard>
  );
};

export const ThirdPartyOnDemandReportSelectionSidebarContent = ({
  selection,
  onSelection,
  smallCardView = false,
  layer,
  labels,
}: ThirdPartyOnDemandReportSelectionSidebarContentProps) => {
  const providerName = (layer.layerConfig as ThirdPartyOnDemandLayerConfig).providerName!;
  const { onSiteSaved } = useWorkbench();
  const siteId =
    (selection.type === SelectionType.SITE || selection.type === SelectionType.SHARED_SITE) && selection?.id
      ? selection?.id
      : "";

  // TODO: Pass in provider, etc to narrow down these orders
  const { data, loading, error } = useThirdPartyOrders({ siteId }, !siteId);
  const order = data?.getThirdPartyOrders.find((order) => order.provider.name === AIDASH_PROVIDER_NAME);

  if (loading) {
    return (
      <div className="flex flex-col gap-y-2 m-2 relative h-[28px]">
        <SkeletonLoading rows={1} showPadding={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2">
        <Notification.Error title="An error occurred" message="There was an issue loading the report." />
      </div>
    );
  }

  return (
    <div>
      {order ? (
        <ThirdPartyOnDemandOrder providerName={providerName} order={order} smallCardView={smallCardView} />
      ) : (
        <UpgradeSection
          selection={selection}
          siteId={siteId}
          onSelection={onSelection}
          onSiteSaved={onSiteSaved}
          smallCardView={smallCardView}
          labels={labels}
        />
      )}
    </div>
  );
};

const UpgradeSection = ({
  selection,
  siteId,
  onSelection,
  onSiteSaved,
  smallCardView = false,
  labels,
}: UpgradeSectionProps) => {
  const { site } = useSiteById(siteId);

  const upgradeConfig = smallCardView
    ? {
        size: "small" as const,
        logoSrc: AiDashLogo,
        imageSrc: undefined,
        listItems: undefined,
        wrapperClasses: "p-0",
        buttonSize: "xs" as const,
        buttonClassName: "font-medium !py-1 mt-2",
      }
    : {
        size: "default" as const,
        logoSrc: undefined,
        imageSrc: AiDashUpgradeLogo,
        listItems: [
          "50% off first order for a limited time",
          "Get a fast, biodiversity baseline report",
          "Spot biodiversity constraints early",
          "Defra-compliant measurement metric",
          "Test uplift scenarios to understand potential credit needs*",
        ],
        wrapperClasses: "flex flex-col gap-y-2 p-2",
        buttonSize: "md" as const,
        buttonClassName: "",
      };

  const handleUpgradeClick = () => {
    updateMapSettings("showBngReport", true);
  };

  const ctaElement = site ? (
    <Button
      variant="secondary"
      size={upgradeConfig.buttonSize}
      onClick={handleUpgradeClick}
      className={clsx(upgradeConfig.buttonClassName, smallCardView && "w-full")}
      fullWidth={smallCardView}
    >
      {smallCardView ? "Learn more" : "Buy report"} <Icon icon="icon-lt-arrow-right-line" />
    </Button>
  ) : (
    <WorkbenchSaveSiteButton
      selection={selection}
      setSelection={onSelection!}
      onSave={onSiteSaved}
      onCompleted={handleUpgradeClick}
      fullWidth
      buttonText="Save site and buy report"
      cardName={AIDASH_CARD_DISPLAY_NAME}
    />
  );

  return (
    <div className={upgradeConfig.wrapperClasses}>
      <UpgradeCTA
        size={upgradeConfig.size}
        imageSrc={upgradeConfig.imageSrc}
        variant="assess"
        logoSrc={upgradeConfig.logoSrc}
        imageAlt={AIDASH_CARD_DISPLAY_NAME}
        listItems={upgradeConfig.listItems}
        title="Quick BNG Assessment with Scenario Planning"
        description="Spot biodiversity risks early with a fast, Defra-compliant preliminary baseline report. Powered by high-resolution commercial grade satellite imagery to avoid delays, cut costs, and protect your GDV."
        ctaElement={ctaElement}
        labels={labels}
      />
      <p className="text-2xs leading-4 text-text-base-secondary-a font-normal pb-2 pt-2 m-0">
        *BNGAI™ account required. Terms and Conditions apply.
      </p>
    </div>
  );
};
